// SPDX-License-Identifier: AGPL-3.0-or-later

import {fileTypeLabel} from '@app/features/channel/components/embeds/attachments/AttachmentFileType';
import {TextualAttachmentPreview} from '@app/features/channel/components/embeds/attachments/TextualAttachmentPreview';
import {splitFilename} from '@app/features/channel/components/embeds/EmbedUtils';
import {canDeleteAttachmentUtil} from '@app/features/channel/components/MessageActionUtils';
import {ATTACHMENT_CARD_WIDTH} from '@app/features/channel/components/MessageAttachmentUtils';
import {useMaybeMessageViewContext} from '@app/features/channel/components/MessageViewContext';
import {DocumentCard, DocumentTooLargeError} from '@app/features/document_preview/DocumentCard';
import {DOCUMENT_PREVIEW_MAX_BYTES, documentKind} from '@app/features/document_preview/DocumentPreviewUtils';
import {FileTypeIcon} from '@app/features/file_preview/FileTypeIcon';
import {DELETE_ATTACHMENT_DESCRIPTOR, DOWNLOAD_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {AttachmentPreviewModal} from '@app/features/messaging/components/AttachmentPreviewModal';
import {useDeleteAttachment} from '@app/features/messaging/hooks/useDeleteAttachment';
import type {Message} from '@app/features/messaging/models/MessagingMessage';
import {shouldPreviewAttachment} from '@app/features/messaging/utils/AttachmentPreviewUtils';
import {downloadFile} from '@app/features/messaging/utils/FileDownloadUtils';
import {formatFileSize} from '@app/features/messaging/utils/FileUtils';
import {remFromPx} from '@app/features/theme/layout/RemFromPx';
import attachmentFileStyles from '@app/features/theme/styles/AttachmentFile.module.css';
import messageStyles from '@app/features/theme/styles/Message.module.css';
import {MediaContextMenu} from '@app/features/ui/action_menu/MediaContextMenu';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {DownloadSimpleIcon, TrashIcon, WarningCircleIcon, XIcon} from '@app/features/ui/icons/Icons';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import type {MessageAttachment} from '@konvo/schema/src/domains/message/MessageResponseSchemas';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';

const ATTACHMENT_EXPIRED_DESCRIPTOR = msg({
	message: 'Attachment expired',
	comment: 'Error message in the channel and chat attachment file.',
});

interface AttachmentFileProps {
	attachment: MessageAttachment;
	isPreview?: boolean;
	message?: Message;
	spoilerHidden?: boolean;
}

export const AttachmentFile = observer(({attachment, message, isPreview, spoilerHidden}: AttachmentFileProps) => {
	const {i18n} = useLingui();
	const {enabled: isMobile} = MobileLayout;
	const isExpired = Boolean(attachment.expired);
	const fileName = attachment.title || attachment.filename;
	const fileUrl = attachment.url ?? attachment.proxy_url;
	// KONVO: HTML pages and Markdown documents show as the page or document (document_preview).
	const docKind =
		!isPreview && !isExpired && !spoilerHidden && fileUrl
			? documentKind(attachment.filename, attachment.content_type)
			: null;
	const showTextPreview = !docKind && !isPreview && shouldPreviewAttachment(attachment);
	let containerStyles: React.CSSProperties;
	if (isMobile) {
		containerStyles = {
			display: 'grid',
			width: '100%',
			maxWidth: '100%',
			minWidth: 0,
		};
	} else if (docKind) {
		containerStyles = {
			display: 'grid',
			width: '100%',
			maxWidth: remFromPx(640),
			minWidth: 0,
		};
	} else if (showTextPreview) {
		containerStyles = {
			display: 'grid',
			width: '100%',
			maxWidth: '50vw',
			minWidth: 0,
		};
	} else {
		containerStyles = {
			display: 'grid',
			width: '100%',
			maxWidth: remFromPx(ATTACHMENT_CARD_WIDTH),
			minWidth: `min(${remFromPx(ATTACHMENT_CARD_WIDTH)}, 100%)`,
		};
	}
	const handlePreview = () => {
		const url = attachment.url ?? attachment.proxy_url;
		if (!url || isExpired || spoilerHidden || isPreview) return;
		ModalCommands.push(
			modal(() => (
				<AttachmentPreviewModal
					attachment={{name: attachment.filename, content_type: attachment.content_type ?? '', size: attachment.size}}
					loadBlob={async () => {
						const response = await fetch(url, {credentials: 'omit'});
						if (!response.ok) throw new Error('Attachment unavailable');
						return response.blob();
					}}
					onClose={() => ModalCommands.pop()}
				/>
			)),
		);
	};
	const handleDownload = async (e: React.MouseEvent) => {
		e.preventDefault();
		const downloadUrl = attachment.proxy_url ?? attachment.url;
		if (!downloadUrl || isExpired) return;
		await downloadFile(downloadUrl, 'file', fileName);
	};
	const handleDelete = useDeleteAttachment(message, attachment.id);
	const canDelete = canDeleteAttachmentUtil(message) && !isMobile;
	const showDeleteButton = canDelete && !isPreview;
	const messageViewContext = useMaybeMessageViewContext();
	const handleMouseDown = () => {
		window.getSelection()?.removeAllRanges();
	};
	const handleContextMenu = (e: React.MouseEvent) => {
		if (!message || isPreview) return;
		e.preventDefault();
		e.stopPropagation();
		ContextMenuCommands.openFromEvent(e, ({onClose}) => (
			<MediaContextMenu
				message={message}
				sourceChannel={messageViewContext?.channel}
				originalSrc={attachment.url ?? ''}
				proxyURL={attachment.proxy_url ?? undefined}
				type="file"
				contentHash={attachment.content_hash}
				attachmentId={attachment.id}
				defaultName={attachment.filename}
				defaultAltText={attachment.filename}
				onClose={onClose}
				onDelete={isPreview ? () => {} : (messageViewContext?.handleDelete ?? (() => {}))}
				data-flx="channel.embeds.attachments.attachment-file.handle-context-menu.media-context-menu.file"
			/>
		));
	};
	return (
		<div
			role="group"
			style={containerStyles}
			className={attachmentFileStyles.container}
			onContextMenu={handleContextMenu}
			onMouseDown={handleMouseDown}
			data-flx="channel.embeds.attachments.attachment-file.group.mouse-down"
		>
			{showDeleteButton && (
				<button
					type="button"
					onClick={handleDelete}
					className={clsx(messageStyles.hoverAction, attachmentFileStyles.deleteButton)}
					aria-label={i18n._(DELETE_ATTACHMENT_DESCRIPTOR)}
					data-flx="channel.embeds.attachments.attachment-file.button.delete"
				>
					<TrashIcon
						size={remFromPx(16)}
						weight="bold"
						data-flx="channel.embeds.attachments.attachment-file.trash-icon"
					/>
				</button>
			)}
			{docKind && fileUrl ? (
				<DocumentCard
					kind={docKind}
					name={fileName}
					size={attachment.size}
					cacheKey={fileUrl}
					load={async (signal) => {
						if (attachment.size > DOCUMENT_PREVIEW_MAX_BYTES) throw new DocumentTooLargeError();
						const response = await fetch(fileUrl, {signal});
						if (!response.ok) throw new Error('Attachment unavailable');
						return response.text();
					}}
					onDownload={() => void downloadFile(attachment.proxy_url ?? fileUrl, 'file', fileName)}
					flx="channel.embeds.attachments.attachment-file.document"
				/>
			) : showTextPreview ? (
				<TextualAttachmentPreview
					attachment={attachment}
					spoilerHidden={spoilerHidden}
					data-flx="channel.embeds.attachments.attachment-file.textual-attachment-preview"
				/>
			) : (
				<FileAttachmentCard
					fileName={fileName}
					contentType={attachment.content_type}
					size={attachment.size}
					onPreview={handlePreview}
					previewDisabled={isExpired || spoilerHidden || isPreview || !(attachment.url || attachment.proxy_url)}
					onDownload={handleDownload}
					downloadDisabled={!attachment.url}
					expired={isExpired}
				/>
			)}
		</div>
	);
});

interface FileAttachmentCardProps {
	fileName: string;
	contentType?: string | null;
	size: number;
	onPreview?: () => void;
	previewDisabled?: boolean;
	onDownload?: (event: React.MouseEvent) => void;
	downloadDisabled?: boolean;
	expired?: boolean;
	/** A file not sent yet (Agents composer): a remove button in place of the download button. */
	onRemove?: () => void;
	removeLabel?: string;
}

/**
 * KONVO: a file as a card, the same in chats and wherever else files show (Agents): its type's icon
 * or logo, name, size and type, opening the attachment preview, with a download button (or a remove
 * button for a file still being added to a message).
 */
export function FileAttachmentCard({
	fileName,
	contentType,
	size,
	onPreview,
	previewDisabled,
	onDownload,
	downloadDisabled,
	expired,
	onRemove,
	removeLabel,
}: FileAttachmentCardProps) {
	const {i18n} = useLingui();
	const fileSize = formatFileSize(size);
	const fileExtension = fileName.split('.').pop()?.toLowerCase() || '';
	const {name: fileNameWithoutExt, extension: fileExt} = splitFilename(fileName);
	return (
		<div className={attachmentFileStyles.attachmentContainer} data-flx="channel.embeds.attachments.attachment-file.div">
			<button
				type="button"
				className={attachmentFileStyles.previewButton}
				onClick={onPreview}
				disabled={previewDisabled}
				aria-label={i18n._(msg`Preview ${fileName}`)}
			>
				<span
					className={attachmentFileStyles.iconContainer}
					data-flx="channel.embeds.attachments.attachment-file.div--2"
				>
					<FileTypeIcon fileName={fileName} contentType={contentType} />
				</span>
				<span
					className={attachmentFileStyles.fileInfoContainer}
					data-flx="channel.embeds.attachments.attachment-file.div--3"
				>
					<span className={attachmentFileStyles.fileName} data-flx="channel.embeds.attachments.attachment-file.p">
						<span
							className={attachmentFileStyles.fileNameTruncate}
							data-flx="channel.embeds.attachments.attachment-file.span"
						>
							{fileNameWithoutExt}
						</span>
						<span
							className={attachmentFileStyles.fileExtension}
							data-flx="channel.embeds.attachments.attachment-file.span--2"
						>
							{fileExt}
						</span>
					</span>
					<span className={attachmentFileStyles.fileSize} data-flx="channel.embeds.attachments.attachment-file.p--2">
						{fileTypeLabel(fileExtension) ? `${fileSize} · ${fileTypeLabel(fileExtension)}` : fileSize}
					</span>
				</span>
			</button>
			{onRemove ? (
				<button
					type="button"
					onClick={onRemove}
					className={attachmentFileStyles.downloadButton}
					aria-label={removeLabel}
					title={removeLabel}
					data-flx="channel.embeds.attachments.attachment-file.button.remove"
				>
					<XIcon size={remFromPx(16)} weight="bold" />
				</button>
			) : expired ? (
				<Tooltip
					text={i18n._(ATTACHMENT_EXPIRED_DESCRIPTOR)}
					data-flx="channel.embeds.attachments.attachment-file.tooltip"
				>
					<div
						className={clsx(attachmentFileStyles.downloadButton, attachmentFileStyles.downloadButtonDisabled)}
						data-flx="channel.embeds.attachments.attachment-file.div--4"
					>
						<WarningCircleIcon
							size={remFromPx(20)}
							weight="bold"
							data-flx="channel.embeds.attachments.attachment-file.warning-circle-icon"
						/>
					</div>
				</Tooltip>
			) : onDownload ? (
				<button
					type="button"
					onClick={onDownload}
					className={attachmentFileStyles.downloadButton}
					aria-label={i18n._(DOWNLOAD_DESCRIPTOR)}
					disabled={downloadDisabled}
					data-flx="channel.embeds.attachments.attachment-file.button.download"
				>
					<DownloadSimpleIcon
						size={remFromPx(18)}
						weight="regular"
						data-flx="channel.embeds.attachments.attachment-file.download-simple-icon"
					/>
				</button>
			) : null}
		</div>
	);
}
